import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { MessageSquare, BarChart3, TrendingUp, Clock, ChevronRight, Play, Target, Loader2, CreditCard, Gift, Mail, FileDown } from "lucide-react";
import { motion } from "framer-motion";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer } from "recharts";
import { NpsSurvey } from "@/components/NpsSurvey";

const TOPIC_LABELS = {
  trinkverlauf: "Trinkverlauf", einsicht: "Einsicht", veraenderung: "Veränderung",
  eigenverantwortung: "Eigenverantwortung", rueckfallpraevention: "Prävention",
  lebenssituation: "Lebenssituation", freie_simulation: "Freie Simulation"
};

const MODE_LABELS = {
  freundlich: "Freundlich", pruefungsnah: "Prüfungsnah", kritisch: "Kritisch",
  intensiv: "Intensiv", unterstuetzend: "Unterstützend"
};

function VerificationBanner() {
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const resend = async () => {
    setSending(true);
    try {
      await api.post("/auth/resend-verification");
      setSent(true);
    } catch { /* ignore */ }
    setSending(false);
  };

  return (
    <div className="rounded-xl border border-warning/30 bg-warning/5 p-4 flex items-start gap-3" data-testid="verification-banner">
      <Mail className="w-5 h-5 text-warning mt-0.5 flex-shrink-0" />
      <div className="flex-1">
        <p className="font-body text-sm font-medium">E-Mail-Adresse noch nicht bestätigt</p>
        <p className="font-body text-xs text-muted-foreground mt-0.5">
          Bitte bestätigen Sie Ihre E-Mail, um alle Funktionen nutzen zu können (z.B. Bezahlung, Stellen-Verbindung).
        </p>
      </div>
      {sent ? (
        <span className="font-body text-xs text-success whitespace-nowrap">Gesendet</span>
      ) : (
        <Button variant="outline" size="sm" className="font-body text-xs whitespace-nowrap" onClick={resend} disabled={sending} data-testid="resend-verification-btn">
          {sending ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : null}
          Erneut senden
        </Button>
      )}
    </div>
  );
}

export default function DashboardPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [progress, setProgress] = useState(null);
  const [subscription, setSubscription] = useState(null);
  const [progressHistory, setProgressHistory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showNps, setShowNps] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [progRes, subRes] = await Promise.all([api.get("/progress"), api.get("/subscription")]);
        setProgress(progRes.data);
        setSubscription(subRes.data);
        // Fetch progress history for chart
        if (progRes.data?.completed_sessions > 0 && user?.user_id) {
          try {
            const histRes = await api.get(`/progress/history/${user.user_id}`);
            setProgressHistory(histRes.data);
          } catch { /* ignore */ }
        }
        // Check NPS eligibility
        try {
          const npsRes = await api.get("/nps/status");
          if (npsRes.data.show_nps) setShowNps(true);
        } catch { /* ignore */ }
      } catch { /* ignore */ }
      setLoading(false);
    };
    fetchData();
  }, []);

  if (loading) return (
    <div className="flex items-center justify-center py-20" data-testid="dashboard-loading">
      <Loader2 className="w-8 h-8 animate-spin text-primary" />
    </div>
  );

  const avg = progress?.average_scores || {};
  const lscores = progress?.learning_scores || {};
  const hasLearningData = Object.values(lscores).some(v => v > 0);
  const overall = hasLearningData ? (lscores.overall_score || 0) : (avg.overall_score || 0);
  const coveredCount = progress?.covered_topics?.length || 0;
  const totalTopics = progress?.all_topics?.length || 7;

  return (
    <div className="page-transition space-y-8" data-testid="dashboard-page">
      {/* NPS Survey Modal */}
      {showNps && <NpsSurvey onClose={() => setShowNps(false)} />}
      {/* Email Verification Banner */}
      {user && !user.email_verified && user.auth_type !== "google" && (
        <VerificationBanner />
      )}
      {/* Berater Trial Welcome Banner */}
      {user?.berater_trial && subscription && !subscription.expired && (
        <div className="flex items-center gap-3 bg-primary/5 border border-primary/20 rounded-lg p-4" data-testid="berater-trial-banner">
          <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
            <Gift className="w-5 h-5 text-primary" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-body text-sm font-medium">Willkommen! Berater-Testzugang aktiv</p>
            <p className="font-body text-xs text-muted-foreground">
              Sie koennen alle Features als Nutzer UND als Beratungsstelle testen.
              {subscription.days_remaining > 0 && ` Noch ${subscription.days_remaining} Tage gueltig.`}
            </p>
          </div>
        </div>
      )}
      {/* Berater Trial Expired Banner */}
      {subscription?.berater_trial_expired_message && (
        <div className="flex items-center gap-3 bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-800 rounded-lg p-4" data-testid="berater-trial-expired-banner">
          <div className="w-10 h-10 rounded-xl bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center flex-shrink-0">
            <Clock className="w-5 h-5 text-amber-600" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-body text-sm font-medium text-amber-800 dark:text-amber-200">Testzugang abgelaufen</p>
            <p className="font-body text-xs text-amber-700 dark:text-amber-300">{subscription.berater_trial_expired_message}</p>
          </div>
          <a href="mailto:info@spurwechsel-pro.de?subject=Berater-Zugang%20Spurwechsel%20PRO" className="flex-shrink-0">
            <Button variant="outline" size="sm" className="font-body text-xs" data-testid="berater-contact-btn">Kontakt</Button>
          </a>
        </div>
      )}
      {/* Welcome */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight">Willkommen, {user?.name?.split(" ")[0]}</h1>
          <p className="font-body text-sm text-muted-foreground mt-1">Hier ist Ihr aktueller Vorbereitungsstand</p>
        </div>
        <Button className="font-body bg-primary text-primary-foreground hover:bg-primary/90 gap-2" onClick={() => navigate("/lernpfad")} data-testid="new-training-btn">
          <Play className="w-4 h-4" /> Training starten
        </Button>
      </div>

      {/* PRIORITÄT 1: Aktivierungs-Prompt für neue Nutzer */}
      {progress?.total_sessions === 0 && (
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}>
          <Card className="border-primary/30 bg-primary/5 shadow-sm" data-testid="activation-prompt">
            <CardContent className="pt-6 pb-5">
              <div className="flex flex-col sm:flex-row items-center gap-6">
                <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                  <MessageSquare className="w-8 h-8 text-primary" />
                </div>
                <div className="flex-1 text-center sm:text-left">
                  <h3 className="font-heading text-xl font-bold mb-1">Starten Sie Ihr erstes Training</h3>
                  <p className="font-body text-sm text-muted-foreground">In einer kurzen Übung erleben Sie, wie ein MPU-Gespräch abläuft. Ein KI-Gesprächspartner stellt Ihnen typische Fragen — es gibt kein Richtig oder Falsch, nur Übung.</p>
                  <p className="font-body text-xs text-muted-foreground mt-2">Dauer: ca. 5 Minuten | Modus: Freundlich und unterstützend | Danach: Persönliches Trainings-Feedback</p>
                </div>
                <Button size="lg" className="font-body bg-primary text-primary-foreground hover:bg-primary/90 gap-2 whitespace-nowrap" onClick={() => navigate("/lernpfad")} data-testid="first-session-cta">
                  <Play className="w-4 h-4" /> Jetzt starten
                </Button>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      )}

      {/* Session Limit / Plan Info */}
      {subscription && subscription.plan === "free" && (
        <Card className="border-border/50 bg-gradient-to-r from-card to-muted/20" data-testid="trial-counter">
          <CardContent className="pt-6 pb-4">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div className="flex items-center gap-4">
                <div className="relative flex-shrink-0">
                  <svg className="w-12 h-12 sm:w-14 sm:h-14 transform -rotate-90" viewBox="0 0 36 36">
                    <circle cx="18" cy="18" r="15" fill="none" stroke="hsl(20,5%,90%)" strokeWidth="3" />
                    <circle cx="18" cy="18" r="15" fill="none" stroke="hsl(131,22%,29%)" strokeWidth="3"
                      strokeDasharray={`${(Math.min(subscription.sessions_used, subscription.sessions_limit) / Math.max(subscription.sessions_limit, 1)) * 94} 94`} strokeLinecap="round" />
                  </svg>
                  <span className="absolute inset-0 flex items-center justify-center font-heading text-xs sm:text-sm font-bold">{subscription.sessions_used}/{subscription.sessions_limit}</span>
                </div>
                <div>
                  <p className="font-body text-sm font-medium">Kostenlose Sessions</p>
                  <p className="font-body text-xs text-muted-foreground">
                    {subscription.sessions_limit - subscription.sessions_used > 0
                      ? `Noch ${subscription.sessions_limit - subscription.sessions_used} verfügbar`
                      : "Sie trainieren weiter — Upgrade für unbegrenzte Sessions"}
                  </p>
                </div>
              </div>
              <Button variant="outline" size="sm" className="font-body gap-1 border-primary text-primary hover:bg-primary hover:text-white w-full sm:w-auto" onClick={() => navigate("/pricing")} data-testid="upgrade-cta">
                <CreditCard className="w-3.5 h-3.5" /> Einmalig ab €39
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Stats Grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          { label: "Sessions gesamt", value: progress?.total_sessions || 0, icon: MessageSquare, color: "text-primary" },
          { label: "Abgeschlossen", value: progress?.completed_sessions || 0, icon: Target, color: "text-success" },
          { label: "Durchschnitt", value: overall ? `${overall}/10` : "--", icon: BarChart3, color: "text-info" },
          { label: "Plan", value: subscription?.plan_name || "Kostenlos", icon: CreditCard, color: "text-warning" },
        ].map((stat, i) => (
          <motion.div key={i} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.08 }}>
            <Card className="border-border/50 hover-lift" data-testid={`stat-card-${i}`}>
              <CardContent className="pt-6 pb-4">
                <div className="flex items-center justify-between mb-2">
                  <stat.icon className={`w-5 h-5 ${stat.color}`} />
                </div>
                <p className="font-heading text-2xl font-bold">{stat.value}</p>
                <p className="font-body text-xs text-muted-foreground">{stat.label}</p>
              </CardContent>
            </Card>
          </motion.div>
        ))}
      </div>

      {/* Quick-Links zu allen Features */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="quick-links">
        {[
          { path: "/akte", label: "MPU-Akte", desc: "Akte hochladen", icon: FileDown },
          { path: "/readiness", label: "Übungsstand", desc: "Bereitschaft prüfen", icon: Target },
          { path: "/knowledge", label: "Wissen", desc: "Grundlagen & FAQ", icon: TrendingUp },
          { path: "/documents", label: "Dokumente", desc: "Unterlagen verwalten", icon: Gift },
        ].map(link => (
          <Card key={link.path} className="border-border/50 cursor-pointer hover:border-primary/20 hover:shadow-sm transition-all" onClick={() => navigate(link.path)} data-testid={`quick-link-${link.path.slice(1)}`}>
            <CardContent className="pt-3 pb-2.5 flex items-center gap-3">
              <div className="w-8 h-8 rounded-lg bg-primary/5 flex items-center justify-center flex-shrink-0">
                <link.icon className="w-4 h-4 text-primary" />
              </div>
              <div className="min-w-0">
                <p className="font-body text-xs font-medium truncate">{link.label}</p>
                <p className="font-body text-[10px] text-muted-foreground truncate">{link.desc}</p>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Score Overview */}
        <Card className="lg:col-span-2 border-border/50" data-testid="scores-card">
          <CardHeader>
            <CardTitle className="font-heading text-lg">Bewertungsübersicht</CardTitle>
            <CardDescription className="font-body text-sm">Ihre Durchschnittswerte über alle abgeschlossenen Sessions</CardDescription>
          </CardHeader>
          <CardContent>
            {progress?.completed_sessions > 0 ? (
              <div className="space-y-4">
                {(hasLearningData ? [
                  { label: "Vorfalldarstellung", value: lscores.vorfall_darstellung || 0 },
                  { label: "Selbstreflexion", value: lscores.selbstreflexion || 0 },
                  { label: "Eigenverantwortung", value: lscores.eigenverantwortung || 0 },
                  { label: "Veränderungsnachweis", value: lscores.veraenderungsnachweis || 0 },
                  { label: "Rückfallprävention", value: lscores.rueckfallpraevention || 0 },
                  { label: "Gesprächskompetenz", value: lscores.gespraechskompetenz || 0 },
                ] : [
                  { label: "Glaubwürdigkeit", value: avg.credibility_score || 0 },
                  { label: "Selbstreflexion", value: avg.reflection_score || 0 },
                  { label: "Eigenverantwortung", value: avg.responsibility_score || 0 },
                  { label: "Veränderungsmotivation", value: avg.motivation_score || 0 },
                  { label: "Rückfallprävention", value: avg.prevention_score || 0 },
                ]).map(item => (
                  <div key={item.label}>
                    <div className="flex justify-between font-body text-sm mb-1">
                      <span>{item.label}</span>
                      <span className="font-medium">{item.value}/10</span>
                    </div>
                    <Progress value={item.value * 10} className="h-2" />
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <BarChart3 className="w-10 h-10 text-muted-foreground/30 mx-auto mb-3" />
                <p className="font-body text-sm text-muted-foreground">Schließen Sie Ihre erste Session ab, um Bewertungen zu sehen</p>
                <Button variant="outline" size="sm" className="mt-4 font-body" onClick={() => navigate("/lernpfad")} data-testid="start-first-session-btn">
                  Erste Session starten
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Topic Coverage */}
        <Card className="border-border/50" data-testid="topics-card">
          <CardHeader>
            <CardTitle className="font-heading text-lg">Themenabdeckung</CardTitle>
            <CardDescription className="font-body text-sm">{coveredCount} von {totalTopics} Themen behandelt</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {Object.entries(TOPIC_LABELS).map(([key, label]) => {
                const covered = progress?.covered_topics?.includes(key);
                return (
                  <div key={key} className={`flex items-center gap-3 p-2.5 rounded-lg transition-colors ${covered ? "bg-primary/5" : "bg-muted/50"}`}>
                    <div className={`w-2 h-2 rounded-full ${covered ? "bg-success" : "bg-border"}`} />
                    <span className={`font-body text-sm ${covered ? "text-foreground" : "text-muted-foreground"}`}>{label}</span>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Trend Chart + Session Limit */}
      {progress?.recent_trend?.length > 1 && (
        <Card className="border-border/50" data-testid="trend-chart-card">
          <CardHeader>
            <CardTitle className="font-heading text-lg">Entwicklungsverlauf</CardTitle>
            <CardDescription className="font-body text-sm">Ihre Gesamtbewertung über die letzten Sessions</CardDescription>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={200}>
              <LineChart data={[...progress.recent_trend].reverse()}>
                <XAxis dataKey="date" tickFormatter={d => new Date(d).toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit" })} className="font-body" tick={{ fontSize: 11 }} />
                <YAxis domain={[0, 10]} tick={{ fontSize: 11 }} />
                <Tooltip labelFormatter={d => new Date(d).toLocaleDateString("de-DE")} formatter={v => [`${v}/10`, "Bewertung"]} />
                <Line type="monotone" dataKey="overall_score" stroke="#1A582E" strokeWidth={2} dot={{ fill: "#1A582E", r: 4 }} />
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      )}

      {/* Kompetenz-Verlauf Chart */}
      {progressHistory?.history?.length > 1 && (
        <Card className="border-border/50" data-testid="competency-history-card">
          <CardHeader>
            <CardTitle className="font-heading text-lg">Kompetenz-Verlauf</CardTitle>
            <CardDescription className="font-body text-sm">Ihre Kompetenzen über alle Sessions</CardDescription>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={220}>
              <LineChart data={progressHistory.history}>
                <XAxis dataKey="date" tickFormatter={d => { const parts = d.split("-"); return `${parts[2]}.${parts[1]}`; }} tick={{ fontSize: 10 }} />
                <YAxis domain={[0, 10]} tick={{ fontSize: 10 }} />
                <Tooltip labelFormatter={d => d} formatter={(v, name) => {
                  const labels = { overall: "Gesamt", vorfall_darstellung: "Vorfall", eigenverantwortung: "Verantwortung", selbstreflexion: "Reflexion", veraenderungsnachweis: "Veränderung", rueckfallpraevention: "Prävention", gespraechskompetenz: "Gespräch" };
                  return [`${v}/10`, labels[name] || name];
                }} />
                <Line type="monotone" dataKey="overall" stroke="#1A582E" strokeWidth={2.5} dot={{ r: 3 }} name="overall" />
                <Line type="monotone" dataKey="eigenverantwortung" stroke="#2563eb" strokeWidth={1} dot={false} strokeDasharray="4 4" name="eigenverantwortung" />
                <Line type="monotone" dataKey="selbstreflexion" stroke="#d97706" strokeWidth={1} dot={false} strokeDasharray="4 4" name="selbstreflexion" />
              </LineChart>
            </ResponsiveContainer>
            <div className="flex flex-wrap gap-3 mt-2 justify-center">
              <span className="flex items-center gap-1 font-body text-xs"><span className="w-3 h-0.5 bg-primary inline-block" /> Gesamt</span>
              <span className="flex items-center gap-1 font-body text-xs text-muted-foreground"><span className="w-3 h-0.5 bg-blue-500 inline-block opacity-50" style={{borderTop: "1px dashed"}} /> Verantwortung</span>
              <span className="flex items-center gap-1 font-body text-xs text-muted-foreground"><span className="w-3 h-0.5 bg-amber-500 inline-block opacity-50" style={{borderTop: "1px dashed"}} /> Reflexion</span>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Session Limit Info */}
      {subscription && (
        <Card className="border-border/50" data-testid="session-limit-card">
          <CardContent className="pt-6 pb-4 flex items-center justify-between">
            <div>
              <p className="font-body text-sm font-medium">Sessions diesen Monat: {subscription.sessions_used} / {subscription.sessions_limit === -1 ? "∞" : subscription.sessions_limit + (subscription.bonus_sessions || 0)}</p>
              <p className="font-body text-xs text-muted-foreground">Plan: {subscription.plan_name}{subscription.bonus_sessions > 0 ? ` (+${subscription.bonus_sessions} Bonus)` : ""}</p>
            </div>
            {subscription.plan === "free" && (
              <Button variant="outline" size="sm" className="font-body gap-1" onClick={() => navigate("/pricing")} data-testid="upgrade-plan-btn">
                <CreditCard className="w-3.5 h-3.5" /> Upgraden
              </Button>
            )}
            {progress?.completed_sessions > 0 && (
              <Button variant="outline" size="sm" className="font-body gap-1" onClick={() => {
                window.open(`${process.env.REACT_APP_BACKEND_URL}/api/reports/progress/${user?.user_id}`, '_blank');
              }} data-testid="download-own-report-btn">
                <FileDown className="w-3.5 h-3.5" /> Trainingsübersicht (PDF)
              </Button>
            )}
          </CardContent>
        </Card>
      )}

      {/* Recent Sessions */}
      <Card className="border-border/50" data-testid="recent-sessions-card">
        <CardHeader>
          <CardTitle className="font-heading text-lg">Letzte Sessions</CardTitle>
        </CardHeader>
        <CardContent>
          {progress?.recent_sessions?.length > 0 ? (
            <div className="space-y-3">
              {progress.recent_sessions.map((s, i) => (
                <div
                  key={s.session_id}
                  className="flex items-center justify-between p-4 rounded-xl border border-border/50 hover:border-primary/20 hover:shadow-sm cursor-pointer transition-all duration-200"
                  onClick={() => s.status === "completed" ? navigate(`/analysis/${s.session_id}`) : navigate(s.type === "learning" ? `/conversation/${s.session_id}` : `/training/${s.session_id}`)}
                  data-testid={`session-row-${i}`}
                >
                  <div className="flex items-center gap-4">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${s.status === "completed" ? "bg-success/10" : "bg-primary/10"}`}>
                      <MessageSquare className={`w-5 h-5 ${s.status === "completed" ? "text-success" : "text-primary"}`} />
                    </div>
                    <div>
                      <p className="font-body text-sm font-medium">{TOPIC_LABELS[s.topic] || s.topic}</p>
                      <div className="flex items-center gap-2 mt-0.5">
                        <Badge variant="secondary" className="text-xs font-body">{MODE_LABELS[s.mode] || s.mode}</Badge>
                        <span className="font-body text-xs text-muted-foreground">{s.message_count || 0} Nachrichten</span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    {s.status === "completed" && s.analysis?.overall_score && (
                      <span className="font-heading text-lg font-bold text-primary">{s.analysis.overall_score}/10</span>
                    )}
                    <Badge variant={s.status === "completed" ? "default" : "outline"} className="font-body text-xs">
                      {s.status === "completed" ? "Abgeschlossen" : "Aktiv"}
                    </Badge>
                    <ChevronRight className="w-4 h-4 text-muted-foreground" />
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8" data-testid="empty-sessions">
              <Clock className="w-10 h-10 text-muted-foreground/30 mx-auto mb-3" />
              <p className="font-body text-sm font-medium mb-1">Noch keine Sessions vorhanden</p>
              <p className="font-body text-xs text-muted-foreground mb-4">Starten Sie Ihr erstes Übungsgespräch im Lernpfad</p>
              <Button size="sm" className="font-body bg-primary text-primary-foreground gap-1.5" onClick={() => navigate("/lernpfad")} data-testid="start-first-session-btn">
                <Play className="w-3.5 h-3.5" /> Erste Session starten
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
